import type { IntlShape } from 'react-intl-next';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';

import type { PageMode } from '@confluence/page-utils/entry-points/enums';
import { UNRESOLVE_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker';
import { type ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker';
import { InlineCommentFramework } from '@confluence/inline-comments-utils/entry-points/enum';
import { ViewValues } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { getLogger } from '@confluence/logger';

import { i18n as inlineCommentsI18n } from './inlineCommentsi18n';

export type ReopenCommentDependencies = {
	reopenInlineCommentFn: (params: {
		variables: { commentId: string; resolved: boolean };
	}) => Promise<any>;
	commentId: string;
	annotationId: string;
	reopenParentComment: (params: { threadKey: string; commentType: CommentType }) => void;
	setOrderedActiveAnnotationIdList: (list: string[]) => void;
	orderedActiveAnnotationIdList: Array<{ threadKey: string }>;
	highlightReopenedComment: (id: string) => void;
	isEditor: boolean;
	eventEmitter: any;
	setActiveHighlight: (id: string | null) => void;
	experienceTracker: ExperienceTrackerAPI;
};

const logger = getLogger('annotation-provider-inline-comment');

export const createHandleReopenComment = (dependencies: ReopenCommentDependencies) => {
	const {
		reopenInlineCommentFn,
		commentId,
		annotationId,
		reopenParentComment,
		setOrderedActiveAnnotationIdList,
		orderedActiveAnnotationIdList,
		highlightReopenedComment,
		isEditor,
		eventEmitter,
		setActiveHighlight,
		experienceTracker,
	} = dependencies;

	return () => {
		return reopenInlineCommentFn({ variables: { commentId, resolved: false } })
			.then(() => {
				reopenParentComment({
					threadKey: annotationId,
					commentType: CommentType.INLINE,
				});
				setOrderedActiveAnnotationIdList([
					...orderedActiveAnnotationIdList.map((status) => status.threadKey),
					annotationId,
				]);
				highlightReopenedComment(annotationId);

				if (isEditor) {
					eventEmitter.emit('unresolve', annotationId);
					eventEmitter.emit('setselectedannotation', annotationId);
				} else {
					const commentElement = document.getElementById(annotationId);
					eventEmitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
						annotationIds: [annotationId],
						eventTarget: commentElement,
					});
					setActiveHighlight(annotationId);
				}
				experienceTracker.succeed({
					name: UNRESOLVE_INLINE_COMMENT_EXPERIENCE,
				});
			})
			.catch((err: any) => {
				logger.error`An error occurred when reopening a comment ${err}`;

				experienceTracker.stopOnError({
					name: UNRESOLVE_INLINE_COMMENT_EXPERIENCE,
					error: err,
				});
			});
	};
};

export const showResolvedCommentFlag = ({
	commentId,
	experienceTracker,
	flags,
	mode,
	annotationId,
	handleReopenComment,
	formatMessage,
	showCommentsPanel,
	setCurrentView,
	scrollCommentsPanelToThread,
	isCommentsPanelShown,
	setCurrentlySelectedThreadKey,
}: {
	commentId: string;
	experienceTracker: ExperienceTrackerAPI;
	flags: FlagsStateContainer;
	mode: PageMode;
	annotationId: string;
	handleReopenComment: () => void;
	formatMessage: IntlShape['formatMessage'];
	showCommentsPanel: (options: { openWithView: ViewValues }) => void;
	setCurrentView: (view: ViewValues) => void;
	scrollCommentsPanelToThread?: (options: {
		annotationId: string;
		annotationClicked?: boolean;
	}) => void;
	isCommentsPanelShown: boolean;
	setCurrentlySelectedThreadKey: (threadKey: string) => void;
}) => {
	void flags?.showSuccessCircleFlag({
		id: commentId,
		title: formatMessage(inlineCommentsI18n.resolvedFlagText),
		isAutoDismiss: true,
		actions: [
			{
				content: formatMessage(inlineCommentsI18n.goToCommentCTA),
				onClick: () => {
					if (!isCommentsPanelShown) {
						setCurrentlySelectedThreadKey(annotationId);
						showCommentsPanel({ openWithView: ViewValues.RESOLVED });
						scrollCommentsPanelToThread?.({ annotationId, annotationClicked: true });
					} else {
						setCurrentView(ViewValues.RESOLVED);
						scrollCommentsPanelToThread?.({ annotationId });
					}

					void flags.hideFlag(flags.state.flags[0]?.id);
				},
			},
			{
				content: formatMessage(inlineCommentsI18n.undoCTA),
				onClick: () => {
					experienceTracker.start({
						name: UNRESOLVE_INLINE_COMMENT_EXPERIENCE,
						id: commentId,
						attributes: {
							mode,
							framework: InlineCommentFramework.ANNOTATION_PROVIDER,
							annotationId,
						},
					});

					handleReopenComment();

					void flags.hideFlag(flags.state.flags[0]?.id);
				},
			},
		],
	});
};
